import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useUnsavedChanges } from "hooks/useUnsavedChanges";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BusinessIcon from "@mui/icons-material/Business";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Fab from "@mui/material/Fab";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";
import { useAuthz } from "context/AuthzContext";
import { PERMISSIONS } from "auth/permissions";
import { useVisionUIController } from "context";
import { TC } from "../tokens/text";
import {
  getIndividualClient,
  updateIndividualClient,
  listTenantUsers,
  listMasterServices,
  listClientServices,
  assignClientService,
  updateClientService,
  removeClientService,
  listClientJobs,
  listJobTasks,
  updateJobTask,
  deleteClientJob,
  extractIndividualIdDocument,
} from "services/tenantUserAdminService";

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { hash: "#personal",    label: "Personal Profile" },
  { hash: "#compliance",  label: "Compliance & KYC" },
  { hash: "#tax",         label: "Tax & Financial" },
];

const RESIDENCY_OPTIONS = [
  { value: "uk_resident",     label: "UK Resident" },
  { value: "non_uk_resident", label: "Non-UK Resident" },
  { value: "dual_resident",   label: "Dual Resident" },
  { value: "non_domiciled",   label: "Non-Domiciled" },
];

const EMPLOYMENT_TYPES = [
  { value: "employed",      label: "Employed" },
  { value: "self_employed", label: "Self-Employed" },
  { value: "director",      label: "Director" },
  { value: "retired",       label: "Retired" },
  { value: "none",          label: "None / Other" },
];

const STUDENT_LOAN_TYPES = [
  { value: "none",    label: "None" },
  { value: "plan1",   label: "Plan 1" },
  { value: "plan2",   label: "Plan 2" },
  { value: "plan4",   label: "Plan 4 (Scotland)" },
  { value: "postgrad", label: "Postgraduate" },
];

const TAX_YEAR_OPTIONS = [
  { value: "2024-25", label: "2024-25" },
  { value: "2025-26", label: "2025-26" },
  { value: "2026-27", label: "2026-27" },
  { value: "2027-28", label: "2027-28" },
];

const SELF_ASSESSMENT_RETURN_TYPES = [
  { value: "sa100", label: "Personal Tax Return-SA100" },
  { value: "sa800", label: "Partnership Tax Return-SA800" },
  { value: "vat100", label: "VAT-100" },
  { value: "mtd-self-assessment", label: "MTD - Self Assessment" },
];

const AML_STATUSES = [
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "verified",    label: "Verified" },
  { value: "failed",      label: "Failed" },
];

const KYC_STATUSES = [
  { value: "not_started", label: "Not Started" },
  { value: "pending",     label: "Pending" },
  { value: "verified",    label: "Verified" },
  { value: "expired",     label: "Expired" },
];

const RISK_RATINGS = [
  { value: "low",    label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high",   label: "High" },
];

const EL_STATUSES = [
  { value: "not_sent", label: "Not Sent" },
  { value: "sent",     label: "Sent" },
  { value: "signed",   label: "Signed" },
];

const PEP_STATUSES = [
  { value: "not_checked", label: "Not Checked" },
  { value: "clear",       label: "Clear" },
  { value: "flagged",     label: "Flagged" },
];

const ACCESS_LEVELS = [
  { value: "partner",   label: "Partner" },
  { value: "senior",    label: "Senior Accountant" },
  { value: "reviewer",  label: "Reviewer" },
  { value: "read_only", label: "Read Only" },
];

const ONBOARDING_STATUS = [
  { value: "pending",  label: "Pending" },
  { value: "partial",  label: "Partial" },
  { value: "complete", label: "Complete" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function labelFor(list, val) {
  return list.find((i) => i.value === val)?.label || val || "—";
}

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    color: TC.primary,
    fontSize: "0.82rem",
    "& fieldset": { bordercolor: "#7a94b0" },
    "&:hover fieldset": { bordercolor: "#4a6080" },
    "&.Mui-focused fieldset": { borderColor: "#0075ff" },
    background: "#f4f8ff",
    borderRadius: "8px",
  },
  "& .MuiInputLabel-root": { color: TC.secondary, fontSize: "0.8rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: TC.link },
};

const SELECT_SX = {
  color: TC.primary, fontSize: "0.875rem",
  background: "#f4f8ff",
  border: "1px solid #d8e6f7",
  borderRadius: "8px",
  "& .MuiSelect-icon": { color: TC.secondary },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover": { background: "#f4f8ff" },
};

const SELECT_MENU_SX = {
  PaperProps: {
    sx: {
      background: "rgba(15,18,59,0.98)",
      border: "1px solid #d8e6f7",
      color: TC.primary,
      zIndex: 1400,
      "& .MuiMenuItem-root:hover": { background: "rgba(0,117,255,0.15)" },
      "& .MuiMenuItem-root.Mui-selected": { background: "rgba(0,117,255,0.25)" },
    },
  },
};

function SectionCard({ children }) {
  return (
    <VuiBox
      p={2.5}
      sx={{
        background: "#f4f8ff",
        border: "1px solid #d8e6f7",
        borderRadius: "12px",
      }}
    >
      {children}
    </VuiBox>
  );
}

function SectionLabel({ children }) {
  return (
    <VuiTypography
      variant="caption" fontWeight="bold"
      sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1.5 }}
    >
      {children}
    </VuiTypography>
  );
}

function ReadRow({ label, value }) {
  if (!value) return null;
  return (
    <Grid item xs={12} sm={6} md={4}>
      <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary,  mb: 0.2 }}>{label}</VuiTypography>
      <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary }}>{value}</VuiTypography>
    </Grid>
  );
}

function StatusChip({ status }) {
  const color = { active: "#4caf50", archived: "#9e9e9e", inactive: "#ff9800" }[status] || "#9e9e9e";
  return (
    <Chip
      label={(status || "unknown").toUpperCase()}
      size="small"
      sx={{ height: 20, fontSize: "0.62rem", fontWeight: 700, bgcolor: `${color}22`, color, border: `1px solid ${color}` }}
    />
  );
}

function InlineToggle({ label, checked, onChange, editing }) {
  return (
    <FormControlLabel
      control={
        <Switch
          checked={Boolean(checked)}
          onChange={(e) => onChange(e.target.checked)}
          disabled={!editing}
          size="small"
          sx={{
            "& .MuiSwitch-track": { background: checked && editing ? "#0075ff" : "rgba(255,255,255,0.2)" },
          }}
        />
      }
      label={<VuiTypography variant="caption" sx={{ color: TC.secondary }}>{label}</VuiTypography>}
      sx={{ mr: 0, ml: 0 }}
    />
  );
}

function EditableSelect({ label, value, onChange, options, editing }) {
  if (!editing) {
    return (
      <Grid item xs={12} sm={6} md={4}>
        <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary,  mb: 0.2 }}>{label}</VuiTypography>
        <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary }}>{labelFor(options, value)}</VuiTypography>
      </Grid>
    );
  }
  return (
    <Grid item xs={12} sm={6} md={4}>
      <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary,  mb: 0.5 }}>{label}</VuiTypography>
      <FormControl fullWidth size="small">
        <Select value={value || ""} onChange={(e) => onChange(e.target.value)} sx={SELECT_SX} MenuProps={SELECT_MENU_SX}>
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>{o.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );
}

function EditableField({ label, value, onChange, editing, type = "text", placeholder }) {
  if (!editing) {
    return (
      <Grid item xs={12} sm={6} md={4}>
        <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary,  mb: 0.2 }}>{label}</VuiTypography>
        <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary }}>{value || "—"}</VuiTypography>
      </Grid>
    );
  }
  return (
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        label={label}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        type={type}
        fullWidth size="small" variant="outlined"
        placeholder={placeholder}
        sx={INPUT_SX}
        InputLabelProps={{ shrink: true }}
      />
    </Grid>
  );
}

// ─── Tab panels ───────────────────────────────────────────────────────────────

function PersonalTab({ client, editing, draft, setDraft, tenantId }) {
  const pp  = editing ? (draft.personalProfile || {}) : (client.personalProfile || {});
  const set = (k, v) => setDraft((d) => ({ ...d, personalProfile: { ...(d.personalProfile || {}), [k]: v } }));

  const fileInputRef               = useRef(null);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted]   = useState(null);
  const [scanError, setScanError]   = useState("");

  async function handleIdUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    setExtracted(null);
    setScanError("");
    try {
      const data = await extractIndividualIdDocument(tenantId, file);
      setDraft((d) => ({
        ...d,
        personalProfile: {
          ...(d.personalProfile || {}),
          ...(data.fullLegalName  && { fullLegalName:  data.fullLegalName }),
          ...(data.dateOfBirth    && { dateOfBirth:    data.dateOfBirth }),
          ...(data.nationality    && { nationality:    data.nationality }),
          ...(data.niNumber       && { niNumber:       data.niNumber }),
          ...(data.addressLine1   && { addressLine1:   data.addressLine1 }),
          ...(data.addressLine2   && { addressLine2:   data.addressLine2 }),
          ...(data.city           && { city:           data.city }),
          ...(data.postcode       && { postcode:       data.postcode }),
          ...(data.country        && { country:        data.country }),
        },
      }));
      const docLabel = {
        passport:         "Passport",
        driving_licence:  "Driving Licence",
        national_id:      "National ID",
        residence_permit: "Residence Permit",
        other:            "ID Document",
      }[data.documentType] || "ID Document";
      setExtracted({
        label:      docLabel,
        number:     data.documentNumber || null,
        expiry:     data.expiryDate     || null,
        confidence: data.confidence     || null,
      });
    } catch (err) {
      const msg = err.message || "Unknown error";
      const isQuota = msg.includes("quota") || msg.includes("429") || msg.includes("billing");
      setScanError(
        isQuota
          ? "Gemini API quota exceeded. Enable billing at ai.google.dev or wait for your quota to reset."
          : `ID extraction failed: ${msg}`
      );
    } finally {
      setExtracting(false);
      e.target.value = "";
    }
  }

  return (
    <VuiBox display="flex" flexDirection="column" gap={2}>

      {/* ── ID Document scan banner ── */}
      <VuiBox
        p={1.5}
        sx={{
          background: "rgba(0,117,255,0.08)",
          border: "1px dashed rgba(0,117,255,0.4)",
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
          style={{ display: "none" }}
          onChange={handleIdUpload}
        />
        <VuiButton
          variant="outlined"
          color="info"
          size="small"
          startIcon={extracting ? <CircularProgress size={14} color="inherit" /> : <DocumentScannerIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={extracting}
          sx={{ whiteSpace: "nowrap" }}
        >
          {extracting ? "Scanning…" : "Scan ID / Passport"}
        </VuiButton>

        {scanError ? (
          <VuiTypography variant="caption" color="error">{scanError}</VuiTypography>
        ) : extracted ? (
          <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 18 }} />
            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
              <strong style={{ color: "#4caf50" }}>Fields auto-filled</strong> from{" "}
              {extracted.label}
              {extracted.number && ` · ${extracted.number}`}
              {extracted.expiry && ` · Expires ${extracted.expiry}`}
              {extracted.confidence != null && ` · ${Math.round(extracted.confidence * 100)}% confidence`}
            </VuiTypography>
          </VuiBox>
        ) : (
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
            Upload a passport, driving licence or national ID to auto-fill the form
          </VuiTypography>
        )}
      </VuiBox>
      <SectionCard>
        <SectionLabel>Identity</SectionLabel>
        <Grid container spacing={2}>
          <EditableField label="Full Legal Name" value={pp.full_legal_name} onChange={(v) => set("fullLegalName", v)} editing={editing} />
          <EditableField label="Preferred Name" value={pp.preferred_name} onChange={(v) => set("preferredName", v)} editing={editing} />
          <EditableField label="Date of Birth" value={pp.date_of_birth} onChange={(v) => set("dateOfBirth", v)} editing={editing} type="date" />
          <EditableField label="Nationality" value={pp.nationality} onChange={(v) => set("nationality", v)} editing={editing} />
          <EditableSelect label="Residency Status" value={pp.residency_status} onChange={(v) => set("residencyStatus", v)} options={RESIDENCY_OPTIONS} editing={editing} />
        </Grid>
      </SectionCard>

      <SectionCard>
        <SectionLabel>Tax References</SectionLabel>
        <Grid container spacing={2}>
          <EditableField label="National Insurance Number" value={pp.ni_number} onChange={(v) => set("niNumber", v)} editing={editing} placeholder="AB 12 34 56 C" />
          <EditableField label="UTR (Unique Taxpayer Reference)" value={pp.utr} onChange={(v) => set("utr", v)} editing={editing} placeholder="10-digit UTR" />
        </Grid>
      </SectionCard>

      <SectionCard>
        <SectionLabel>Contact Details</SectionLabel>
        <Grid container spacing={2}>
          <EditableField label="Email" value={pp.email} onChange={(v) => set("email", v)} editing={editing} type="email" />
          <EditableField label="Mobile" value={pp.mobile} onChange={(v) => set("mobile", v)} editing={editing} />
          <EditableField label="Landline" value={pp.landline} onChange={(v) => set("landline", v)} editing={editing} />
        </Grid>
      </SectionCard>

      <SectionCard>
        <SectionLabel>Address</SectionLabel>
        <Grid container spacing={2}>
          <EditableField label="Address Line 1" value={pp.address_line1} onChange={(v) => set("addressLine1", v)} editing={editing} />
          <EditableField label="Address Line 2" value={pp.address_line2} onChange={(v) => set("addressLine2", v)} editing={editing} />
          <EditableField label="City" value={pp.city} onChange={(v) => set("city", v)} editing={editing} />
          <EditableField label="Postcode" value={pp.postcode} onChange={(v) => set("postcode", v)} editing={editing} />
          <EditableField label="Country" value={pp.country} onChange={(v) => set("country", v)} editing={editing} />
        </Grid>
      </SectionCard>
    </VuiBox>
  );
}

function ComplianceTab({ client, editing, draft, setDraft }) {
  const cf  = editing ? (draft.compliance || {}) : (client.compliance || {});
  const set = (k, v) => setDraft((d) => ({ ...d, compliance: { ...(d.compliance || {}), [k]: v } }));

  const statusColor = { verified: "#4caf50", failed: "#f44336", in_progress: "#2196f3", not_started: "#9e9e9e", pending: "#ff9800", expired: "#ff9800" };

  function StatusPill({ value, options }) {
    const label = labelFor(options, value);
    const color = statusColor[value] || "#9e9e9e";
    return (
      <Chip
        label={label.toUpperCase()}
        size="small"
        sx={{ height: 20, fontSize: "0.62rem", fontWeight: 700, bgcolor: `${color}22`, color, border: `1px solid ${color}` }}
      />
    );
  }

  return (
    <VuiBox display="flex" flexDirection="column" gap={2}>
      <SectionCard>
        <SectionLabel>AML / KYC Status</SectionLabel>
        <Grid container spacing={2}>
          {!editing ? (
            <>
              <ReadRow label="AML Status" value={<StatusPill value={cf.aml_status} options={AML_STATUSES} />} />
              <ReadRow label="KYC Status" value={<StatusPill value={cf.kyc_status} options={KYC_STATUSES} />} />
              <ReadRow label="Risk Rating" value={<StatusPill value={cf.risk_rating} options={RISK_RATINGS} />} />
              <ReadRow label="PEP Status" value={<StatusPill value={cf.pep_status} options={PEP_STATUSES} />} />
              <ReadRow label="Sanctions Status" value={<StatusPill value={cf.sanctions_status} options={PEP_STATUSES} />} />
            </>
          ) : (
            <>
              <EditableSelect label="AML Status" value={cf.aml_status} onChange={(v) => set("amlStatus", v)} options={AML_STATUSES} editing />
              <EditableSelect label="KYC Status" value={cf.kyc_status} onChange={(v) => set("kycStatus", v)} options={KYC_STATUSES} editing />
              <EditableSelect label="Risk Rating" value={cf.risk_rating} onChange={(v) => set("riskRating", v)} options={RISK_RATINGS} editing />
              <EditableSelect label="PEP Status" value={cf.pep_status} onChange={(v) => set("pepStatus", v)} options={PEP_STATUSES} editing />
              <EditableSelect label="Sanctions Status" value={cf.sanctions_status} onChange={(v) => set("sanctionsStatus", v)} options={PEP_STATUSES} editing />
            </>
          )}
        </Grid>
      </SectionCard>

      <SectionCard>
        <SectionLabel>Engagement & Consent</SectionLabel>
        <Grid container spacing={2}>
          {!editing ? (
            <>
              <ReadRow label="Engagement Letter" value={<StatusPill value={cf.engagement_letter_status} options={EL_STATUSES} />} />
              <ReadRow label="GDPR Consent" value={cf.gdpr_consent ? "✓ Obtained" : "✗ Not obtained"} />
            </>
          ) : (
            <>
              <EditableSelect label="Engagement Letter" value={cf.engagement_letter_status} onChange={(v) => set("engagementLetterStatus", v)} options={EL_STATUSES} editing />
              <Grid item xs={12} sm={6}>
                <InlineToggle
                  label="GDPR Consent Obtained"
                  checked={cf.gdpr_consent}
                  onChange={(v) => set("gdprConsent", v)}
                  editing={editing}
                />
              </Grid>
            </>
          )}
        </Grid>
      </SectionCard>
    </VuiBox>
  );
}

function TaxTab({ client, editing, draft, setDraft }) {
  const tf  = editing ? (draft.taxFinancial || {}) : (client.taxFinancial || {});
  const set = (k, v) => setDraft((d) => ({ ...d, taxFinancial: { ...(d.taxFinancial || {}), [k]: v } }));

  return (
    <VuiBox display="flex" flexDirection="column" gap={2}>
      <SectionCard sx={{ position: 'relative', zIndex: 1, '& .MuiRadio-root, & input[type=radio], & .MuiSwitch-root': { pointerEvents: 'auto' } }}>
        <SectionLabel>Return Setup</SectionLabel>
        <VuiTypography variant="caption" sx={{ color: TC.secondary, display: "block", mb: 1.5 }}>
          Choose the relevant tax year and return journey before completing the detailed self-assessment data.
        </VuiTypography>
        <Grid container spacing={2}>
          <EditableSelect label="Relevant Tax Year" value={tf.tax_year} onChange={(v) => set("tax_year", v)} options={TAX_YEAR_OPTIONS} editing={editing} />
          <EditableSelect label="Return Type" value={tf.return_type} onChange={(v) => set("return_type", v)} options={SELF_ASSESSMENT_RETURN_TYPES} editing={editing} />
        </Grid>
      </SectionCard>

      <SectionCard sx={{ position: 'relative', zIndex: 1, '& .MuiRadio-root, & input[type=radio], & .MuiSwitch-root': { pointerEvents: 'auto' } }}>
        <SectionLabel>Self Assessment & Employment</SectionLabel>
        <VuiTypography variant="caption" sx={{ color: TC.secondary, display: "block", mb: 1.5 }}>
          These fields auto-populate from the client profile when available and can be completed manually for draft SA preparation.
        </VuiTypography>
        <Grid container spacing={2}>
          <EditableSelect label="Employment Type" value={tf.employment_type} onChange={(v) => set("employment_type", v)} options={EMPLOYMENT_TYPES} editing={editing} />
          <EditableSelect label="Student Loan Type" value={tf.student_loan_type} onChange={(v) => set("student_loan_type", v)} options={STUDENT_LOAN_TYPES} editing={editing} />
        </Grid>
        <VuiBox mt={2} display="flex" flexWrap="wrap" gap={3}>
          <InlineToggle label="SA Registered" checked={tf.sa_registered} onChange={(v) => set("sa_registered", v)} editing={editing} />
          <InlineToggle label="UTR Verified" checked={tf.utr_verified} onChange={(v) => set("utr_verified", v)} editing={editing} />
          <InlineToggle label="Has Employment Income" checked={tf.has_employment_income} onChange={(v) => set("has_employment_income", v)} editing={editing} />
          <InlineToggle label="Has Self-Employment Income" checked={tf.has_self_employment_income} onChange={(v) => set("has_self_employment_income", v)} editing={editing} />
          <InlineToggle label="Has Property Income" checked={tf.has_property_income} onChange={(v) => set("has_property_income", v)} editing={editing} />
          <InlineToggle label="Has Savings Income" checked={tf.has_savings_income} onChange={(v) => set("has_savings_income", v)} editing={editing} />
          <InlineToggle label="Has Dividend Income" checked={tf.has_dividend_income} onChange={(v) => set("has_dividend_income", v)} editing={editing} />
          <InlineToggle label="Has Investment Income" checked={tf.has_investment_income} onChange={(v) => set("has_investment_income", v)} editing={editing} />
          <InlineToggle label="Has Pension Income" checked={tf.has_pension_income} onChange={(v) => set("has_pension_income", v)} editing={editing} />
          <InlineToggle label="Has Capital Gains" checked={tf.has_capital_gains} onChange={(v) => set("has_capital_gains", v)} editing={editing} />
          <InlineToggle label="Has Foreign Income" checked={tf.has_foreign_income} onChange={(v) => set("has_foreign_income", v)} editing={editing} />
          <InlineToggle label="Has CIS Income" checked={tf.has_cis_income} onChange={(v) => set("has_cis_income", v)} editing={editing} />
          <InlineToggle label="Has Other Income" checked={tf.has_other_income} onChange={(v) => set("has_other_income", v)} editing={editing} />
          <InlineToggle label="Child Benefit" checked={tf.child_benefit} onChange={(v) => set("child_benefit", v)} editing={editing} />
          <InlineToggle label="Marriage Allowance" checked={tf.marriage_allowance} onChange={(v) => set("marriage_allowance", v)} editing={editing} />
          <InlineToggle label="Blind Person Allowance" checked={tf.blind_person_allowance} onChange={(v) => set("blind_person_allowance", v)} editing={editing} />
          <InlineToggle label="High Income Child Benefit Charge" checked={tf.high_income_child_benefit_charge} onChange={(v) => set("high_income_child_benefit_charge", v)} editing={editing} />
        </VuiBox>
      </SectionCard>

      <SectionCard sx={{ position: 'relative', zIndex: 1, '& .MuiRadio-root, & input[type=radio], & .MuiSwitch-root': { pointerEvents: 'auto' } }}>
        <SectionLabel>Employment & PAYE</SectionLabel>
        <Grid container spacing={2}>
          <EditableField label="PAYE Reference" value={tf.employment_paye_reference} onChange={(v) => set("employment_paye_reference", v)} editing={editing} />
          <EditableField label="Employment Income" value={tf.employment_income} onChange={(v) => set("employment_income", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Tax Deducted" value={tf.employment_tax_deducted} onChange={(v) => set("employment_tax_deducted", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Benefits in Kind" value={tf.benefits_in_kind} onChange={(v) => set("benefits_in_kind", v)} editing={editing} placeholder="0.00" />
        </Grid>
      </SectionCard>

      <SectionCard sx={{ position: 'relative', zIndex: 1, '& .MuiRadio-root, & input[type=radio], & .MuiSwitch-root': { pointerEvents: 'auto' } }}>
        <SectionLabel>Self-Employment</SectionLabel>
        <Grid container spacing={2}>
          <EditableField label="Business Name" value={tf.self_employment_business_name} onChange={(v) => set("self_employment_business_name", v)} editing={editing} />
          <EditableField label="Business Description" value={tf.self_employment_business_description} onChange={(v) => set("self_employment_business_description", v)} editing={editing} />
          <EditableField label="Turnover" value={tf.self_employment_turnover} onChange={(v) => set("self_employment_turnover", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Allowable Expenses" value={tf.self_employment_expenses} onChange={(v) => set("self_employment_expenses", v)} editing={editing} placeholder="0.00" />
          <EditableField label="CIS Deductions Suffered" value={tf.cis_deductions_suffered} onChange={(v) => set("cis_deductions_suffered", v)} editing={editing} placeholder="0.00" />
        </Grid>
      </SectionCard>

      <SectionCard sx={{ position: 'relative', zIndex: 1, '& .MuiRadio-root, & input[type=radio], & .MuiSwitch-root': { pointerEvents: 'auto' } }}>
        <SectionLabel>Property, Investments & Other Income</SectionLabel>
        <Grid container spacing={2}>
          <EditableField label="Property Income" value={tf.property_income} onChange={(v) => set("property_income", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Property Expenses" value={tf.property_expenses} onChange={(v) => set("property_expenses", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Savings Interest" value={tf.savings_interest} onChange={(v) => set("savings_interest", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Dividend Income" value={tf.dividend_income} onChange={(v) => set("dividend_income", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Pension Income" value={tf.pension_income} onChange={(v) => set("pension_income", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Foreign Income" value={tf.foreign_income} onChange={(v) => set("foreign_income", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Capital Gains" value={tf.capital_gains} onChange={(v) => set("capital_gains", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Other Income" value={tf.other_income} onChange={(v) => set("other_income", v)} editing={editing} placeholder="0.00" />
        </Grid>
      </SectionCard>

      <SectionCard sx={{ position: 'relative', zIndex: 1, '& .MuiRadio-root, & input[type=radio], & .MuiSwitch-root': { pointerEvents: 'auto' } }}>
        <SectionLabel>Reliefs, Deductions & Payments</SectionLabel>
        <Grid container spacing={2}>
          <EditableField label="Pension Contributions" value={tf.pension_contributions} onChange={(v) => set("pension_contributions", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Gift Aid Donations" value={tf.gift_aid_donations} onChange={(v) => set("gift_aid_donations", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Payments on Account Made" value={tf.payments_on_account_made} onChange={(v) => set("payments_on_account_made", v)} editing={editing} placeholder="0.00" />
          <EditableField label="Tax Already Paid" value={tf.tax_already_paid} onChange={(v) => set("tax_already_paid", v)} editing={editing} placeholder="0.00" />
        </Grid>
      </SectionCard>

      <SectionCard sx={{ position: 'relative', zIndex: 1, '& .MuiRadio-root, & input[type=radio], & .MuiSwitch-root': { pointerEvents: 'auto' } }}>
        <SectionLabel>Bank Details (for refunds)</SectionLabel>
        <Grid container spacing={2}>
          <EditableField label="Account Name" value={tf.bank_account_name} onChange={(v) => set("bank_account_name", v)} editing={editing} />
          <EditableField label="Sort Code" value={tf.bank_sort_code} onChange={(v) => set("bank_sort_code", v)} editing={editing} placeholder="00-00-00" />
          <EditableField label="Account Number" value={tf.bank_account_number} onChange={(v) => set("bank_account_number", v)} editing={editing} placeholder="12345678" />
        </Grid>
      </SectionCard>
    </VuiBox>
  );
}

// ─── Services Tab ─────────────────────────────────────────────────────────────

const SERVICE_COLORS = {
  accounts:            "#4caf50",
  corporation_tax:     "#2196f3",
  vat:                 "#9c27b0",
  payroll:             "#ff9800",
  self_assessment:     "#00bcd4",
  company_secretarial: "#607d8b",
  mtd_quarterly:       "#e91e63",
};

function ServiceStatusChip({ status }) {
  const cfg = {
    active:    { color: "#4caf50", label: "Active" },
    paused:    { color: "#ff9800", label: "Paused" },
    cancelled: { color: "#9e9e9e", label: "Cancelled" },
  }[status] || { color: "#9e9e9e", label: status };
  return (
    <Chip label={cfg.label.toUpperCase()} size="small" sx={{
      height: 18, fontSize: "0.6rem", fontWeight: 700,
      bgcolor: `${cfg.color}22`, color: cfg.color, border: `1px solid ${cfg.color}`,
    }} />
  );
}

function ServicesTab({ client, tenantId, canManage }) {
  const [controller] = useVisionUIController();
  const { serviceDefaults } = controller;

  const [masterServices, setMasterServices] = useState([]);
  const [assignments, setAssignments]       = useState([]);
  const [loading, setLoading]               = useState(true);
  const [assigning, setAssigning]           = useState(false);
  const [assignError, setAssignError]       = useState("");
  const [removeId, setRemoveId]             = useState(null);

  // Manager picker dialog state
  const [pickerOpen, setPickerOpen]         = useState(false);
  const [pendingServiceId, setPendingServiceId] = useState(null);
  const [tenantUsers, setTenantUsers]       = useState([]);
  const [usersLoading, setUsersLoading]     = useState(false);
  const [selectedManager, setSelectedManager] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [ms, as] = await Promise.all([
        listMasterServices(),
        listClientServices(tenantId, client.id),
      ]);
      setMasterServices(ms.services || []);
      setAssignments(as.assignments || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (tenantId && client?.id) load(); }, [tenantId, client?.id]);

  const activeIds = new Set(assignments.filter((a) => a.status === "active").map((a) => a.serviceId));

  async function openPicker(serviceId) {
    setPendingServiceId(serviceId);
    // Pre-fill with the default assignee for this service type (if one is set)
    const def = serviceDefaults?.[serviceId];
    setSelectedManager(def?.uid || "");
    setAssignError("");
    setPickerOpen(true);
    if (tenantUsers.length === 0) {
      setUsersLoading(true);
      try {
        const res = await listTenantUsers({ tenantId });
        setTenantUsers(res.users || res || []);
      } catch (_) {
        setTenantUsers([]);
      } finally {
        setUsersLoading(false);
      }
    }
  }

  async function confirmAssign() {
    setAssignError("");
    setAssigning(true);
    const mgr = tenantUsers.find((u) => u.uid === selectedManager);
    try {
      await assignClientService(tenantId, client.id, {
        serviceId:           pendingServiceId,
        clientName:          client.legalName || client.id,
        assignedManagerUid:  mgr?.uid  || null,
        assignedManagerName: mgr?.displayName || mgr?.email || null,
      });
      setPickerOpen(false);
      await load();
    } catch (e) {
      setAssignError(e.message);
    } finally {
      setAssigning(false);
    }
  }

  async function handleRemove(assignmentId) {
    setRemoveId(assignmentId);
    try {
      await removeClientService(tenantId, client.id, assignmentId);
      await load();
    } finally {
      setRemoveId(null);
    }
  }

  if (loading) return <VuiBox p={4} display="flex" justifyContent="center"><CircularProgress size={28} /></VuiBox>;

  return (
    <VuiBox display="flex" flexDirection="column" gap={3}>

      {/* Manager picker dialog */}
      {pickerOpen && (
        <VuiBox sx={{
          position: "fixed", inset: 0, zIndex: 1300,
          background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <VuiBox sx={{
            background: "linear-gradient(135deg,#0f1535,#1a1f3a)",
            border: "1px solid #d8e6f7",
            borderRadius: "16px", p: 3, width: 360, display: "flex", flexDirection: "column", gap: 2,
          }}>
            <VuiTypography variant="h6" fontWeight="bold" sx={{ color: TC.primary }}>
              Assign Service
            </VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
              {masterServices.find((s) => s.id === pendingServiceId)?.name} → {client.legalName || client.id}
            </VuiTypography>

            <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>
              Assign Manager (optional)
              {serviceDefaults?.[pendingServiceId] && (
                <span style={{ color: "rgba(247,183,49,0.85)", fontWeight: 400, marginLeft: 6, fontSize: "0.7rem" }}>
                  ★ default pre-filled
                </span>
              )}
            </VuiTypography>
            {usersLoading ? (
              <VuiBox display="flex" justifyContent="center" py={1}><CircularProgress size={22} /></VuiBox>
            ) : (
              <FormControl fullWidth size="small">
                <Select
                  value={selectedManager}
                  onChange={(e) => setSelectedManager(e.target.value)}
                  displayEmpty
                  sx={{
                    color: TC.primary, fontSize: "0.82rem",
                    background: "#f4f8ff",
                    border: "1px solid #d8e6f7",
                    borderRadius: "8px",
                    ".MuiSelect-icon": { color: "#fff" },
                  }}
                >
                  <MenuItem value=""><em style={{ color: "#aaa" }}>No manager</em></MenuItem>
                  {tenantUsers.map((u) => (
                    <MenuItem key={u.uid} value={u.uid}>
                      {u.displayName || u.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {assignError && (
              <VuiTypography variant="caption" color="error">{assignError}</VuiTypography>
            )}

            <VuiBox display="flex" gap={1} justifyContent="flex-end" mt={1}>
              <VuiButton size="small" variant="outlined" color="secondary"
                onClick={() => setPickerOpen(false)} disabled={assigning}>
                Cancel
              </VuiButton>
              <VuiButton size="small" variant="contained" color="info"
                onClick={confirmAssign} disabled={assigning}>
                {assigning ? <CircularProgress size={16} color="inherit" /> : "Confirm"}
              </VuiButton>
            </VuiBox>
          </VuiBox>
        </VuiBox>
      )}

      {/* Available services to add */}
      <SectionCard>
        <SectionLabel>Available Services</SectionLabel>
        <Grid container spacing={2}>
          {masterServices.map((svc) => {
            const isActive = activeIds.has(svc.id);
            const color    = SERVICE_COLORS[svc.id] || "#0075ff";
            return (
              <Grid key={svc.id} item xs={12} sm={6} md={4}>
                <VuiBox p={1.5} sx={{
                  background: isActive ? `${color}15` : "rgba(255,255,255,0.03)",
                  border: `1px solid ${isActive ? color : "rgba(255,255,255,0.08)"}`,
                  borderRadius: "10px", transition: "all 0.2s",
                  display: "flex", flexDirection: "column", gap: 0.5,
                }}>
                  <VuiBox display="flex" justifyContent="space-between" alignItems="center">
                    <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary,  fontSize: "0.82rem" }}>
                      {svc.name}
                    </VuiTypography>
                    <Chip label={svc.frequency} size="small" sx={{
                      height: 16, fontSize: "0.58rem", fontWeight: 600,
                      bgcolor: `${color}22`, color, border: `1px solid ${color}44`,
                    }} />
                  </VuiBox>
                  <VuiTypography variant="caption" sx={{ color: TC.secondary,  mb: 1 }}>{svc.description}</VuiTypography>
                  {canManage && (
                    isActive
                      ? <VuiTypography variant="caption" sx={{ color: "#4caf50", fontWeight: 700 }}>✓ Assigned</VuiTypography>
                      : (
                        <VuiButton size="small" variant="outlined" color="info"
                          onClick={() => openPicker(svc.id)} disabled={assigning}
                          sx={{ py: 0.3, fontSize: "0.72rem", mt: 0.5 }}>
                          + Assign
                        </VuiButton>
                      )
                  )}
                </VuiBox>
              </Grid>
            );
          })}
        </Grid>
      </SectionCard>

      {/* Assigned services list */}
      {assignments.length > 0 && (
        <SectionCard>
          <SectionLabel>Assigned Services</SectionLabel>
          <VuiBox display="flex" flexDirection="column" gap={1.5}>
            {assignments.map((a) => {
              const color = SERVICE_COLORS[a.serviceId] || "#0075ff";
              return (
                <VuiBox key={a.id} p={1.5} sx={{
                  background: "#f4f8ff",
                  border: `1px solid rgba(255,255,255,0.08)`,
                  borderLeft: `3px solid ${color}`,
                  borderRadius: "8px",
                  display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1,
                }}>
                  <VuiBox display="flex" alignItems="center" gap={1.5}>
                    <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary,  fontSize: "0.83rem" }}>
                      {a.serviceName}
                    </VuiTypography>
                    <ServiceStatusChip status={a.status} />
                    <VuiTypography variant="caption" sx={{ color: TC.secondary }}>Since {a.startDate}</VuiTypography>
                    {a.assignedManagerName && (
                      <Chip
                        label={`👤 ${a.assignedManagerName}`}
                        size="small"
                        sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#7a94b0", color: "#ccc" }}
                      />
                    )}
                  </VuiBox>
                  {canManage && a.status !== "cancelled" && (
                    <VuiButton size="small" variant="outlined" color="error"
                      onClick={() => handleRemove(a.id)}
                      disabled={removeId === a.id}
                      sx={{ py: 0.3, fontSize: "0.7rem" }}>
                      {removeId === a.id ? "Removing…" : "Remove"}
                    </VuiButton>
                  )}
                </VuiBox>
              );
            })}
          </VuiBox>
        </SectionCard>
      )}
    </VuiBox>
  );
}

// ─── Jobs & Tasks Tab ─────────────────────────────────────────────────────────

const JOB_STATUS_CFG = {
  pending:     { color: "#ff9800", label: "Pending" },
  in_progress: { color: "#2196f3", label: "In Progress" },
  review:      { color: "#9c27b0", label: "Review" },
  approved:    { color: "#00bcd4", label: "Approved" },
  filed:       { color: "#4caf50", label: "Filed" },
  invoiced:    { color: "#4caf50", label: "Invoiced" },
  cancelled:   { color: "#9e9e9e", label: "Cancelled" },
};

function JobCard({ job, tenantId }) {
  const [expanded, setExpanded] = useState(false);
  const [tasks, setTasks]       = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [updatingTask, setUpdatingTask] = useState(null);

  async function loadTasks() {
    if (tasks.length) { setExpanded(!expanded); return; }
    setExpanded(true);
    setTasksLoading(true);
    try {
      const res = await listJobTasks(tenantId, job.id);
      setTasks(res.tasks || []);
    } finally {
      setTasksLoading(false);
    }
  }

  async function toggleTask(task) {
    const next = task.status === "done" ? "pending" : "done";
    setUpdatingTask(task.id);
    try {
      await updateJobTask(tenantId, job.id, task.id, { status: next });
      setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, status: next } : t));
    } finally {
      setUpdatingTask(null);
    }
  }

  const cfg          = JOB_STATUS_CFG[job.status] || JOB_STATUS_CFG.pending;
  const pct          = job.taskCount ? Math.round((job.completedCount / job.taskCount) * 100) : 0;
  const svcColor     = SERVICE_COLORS[job.serviceId] || "#0075ff";
  const isOverdue    = job.dueDate && new Date(job.dueDate) < new Date() && job.status !== "filed";

  return (
    <VuiBox sx={{
      background: "#f4f8ff",
      border: `1px solid rgba(255,255,255,0.08)`,
      borderLeft: `3px solid ${svcColor}`,
      borderRadius: "10px", overflow: "hidden",
    }}>
      {/* Header */}
      <VuiBox p={1.5} display="flex" alignItems="center" justifyContent="space-between"
        onClick={loadTasks} sx={{ cursor: "pointer", "&:hover": { background: "#f4f8ff" } }}>
        <VuiBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary,  fontSize: "0.85rem" }}>
            {job.title}
          </VuiTypography>
          <Chip label={cfg.label.toUpperCase()} size="small" sx={{
            height: 18, fontSize: "0.6rem", fontWeight: 700,
            bgcolor: `${cfg.color}22`, color: cfg.color, border: `1px solid ${cfg.color}`,
          }} />
          {isOverdue && (
            <Chip label="OVERDUE" size="small" sx={{
              height: 18, fontSize: "0.6rem", fontWeight: 700,
              bgcolor: "rgba(244,67,54,0.15)", color: "#f44336", border: "1px solid #f44336",
            }} />
          )}
        </VuiBox>
        <VuiBox display="flex" alignItems="center" gap={2}>
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
            Due: <strong style={{ color: isOverdue ? "#f44336" : "white" }}>{job.dueDate}</strong>
          </VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{pct}%</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{expanded ? "▲" : "▼"}</VuiTypography>
        </VuiBox>
      </VuiBox>

      {/* Progress bar */}
      <VuiBox sx={{ height: 3, background: "#f4f8ff" }}>
        <VuiBox sx={{ height: 3, width: `${pct}%`, background: svcColor, transition: "width 0.3s" }} />
      </VuiBox>

      {/* Tasks */}
      {expanded && (
        <VuiBox p={1.5} pt={1} display="flex" flexDirection="column" gap={0.5}>
          {tasksLoading ? (
            <VuiBox display="flex" justifyContent="center" py={1}><CircularProgress size={20} /></VuiBox>
          ) : (
            tasks.map((task) => (
              <VuiBox key={task.id} display="flex" alignItems="center" gap={1.5}
                onClick={() => toggleTask(task)}
                sx={{ cursor: "pointer", opacity: updatingTask === task.id ? 0.5 : 1, p: 0.8,
                  borderRadius: "6px", "&:hover": { background: "#f4f8ff" } }}>
                <VuiBox sx={{
                  width: 16, height: 16, borderRadius: "4px", flexShrink: 0,
                  border: task.status === "done" ? "none" : "2px solid rgba(255,255,255,0.3)",
                  background: task.status === "done" ? "#4caf50" : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {task.status === "done" && <span style={{ color: TC.primary, fontSize: 10, lineHeight: 1 }}>✓</span>}
                </VuiBox>
                <VuiTypography variant="caption" color={task.status === "done" ? "text" : "white"}
                  sx={{ textDecoration: task.status === "done" ? "line-through" : "none", fontSize: "0.8rem" }}>
                  {task.title}
                </VuiTypography>
              </VuiBox>
            ))
          )}
        </VuiBox>
      )}
    </VuiBox>
  );
}

function JobsTab({ client, tenantId }) {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    if (!tenantId || !client?.id) return;
    setLoading(true);
    listClientJobs(tenantId, client.id)
      .then((res) => setJobs(res.jobs || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [tenantId, client?.id]);

  if (loading) return <VuiBox p={4} display="flex" justifyContent="center"><CircularProgress size={28} /></VuiBox>;

  return (
    <VuiBox display="flex" flexDirection="column" gap={2}>
      <SectionCard>
        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SectionLabel>Jobs & Tasks</SectionLabel>
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
            {jobs.length} job{jobs.length !== 1 ? "s" : ""} · Click a job to expand tasks
          </VuiTypography>
        </VuiBox>

        {error && (
          <VuiBox mb={2} p={1} sx={{ background: "rgba(244,67,54,0.1)", borderRadius: "8px" }}>
            <VuiTypography variant="caption" color="error">{error}</VuiTypography>
          </VuiBox>
        )}

        {jobs.length === 0 ? (
          <VuiBox p={3} textAlign="center">
            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
              No jobs yet. Assign services in the Services tab to auto-generate jobs.
            </VuiTypography>
          </VuiBox>
        ) : (
          <VuiBox display="flex" flexDirection="column" gap={1.5}>
            {jobs.map((job) => (
              <JobCard key={job.id} job={job} tenantId={tenantId} />
            ))}
          </VuiBox>
        )}
      </SectionCard>
    </VuiBox>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function IndividualClientDetail() {
  const { clientId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { tenantId: activeTenantId, hasAllPermissions } = useAuthz();

  const [client, setClient]   = useState(null);
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [saveError, setSaveError] = useState("");
  const [draft, setDraft]     = useState({});
  const { ConfirmDialog } = useUnsavedChanges(editing);

  const canManage = hasAllPermissions([PERMISSIONS.MANAGE_CLIENT_PROFILES]);

  // URL-hash driven tab
  const currentHash = location.hash || TABS[0].hash;
  const tabIndex = Math.max(0, TABS.findIndex((t) => t.hash === currentHash));

  function handleTabChange(_, idx) {
    history.replace({ ...location, hash: TABS[idx].hash });
  }

  const load = useCallback(async () => {
    if (!activeTenantId || !clientId) return;
    setLoading(true);
    setError("");
    try {
      const [clientRes, usersRes] = await Promise.all([
        getIndividualClient(activeTenantId, clientId),
        listTenantUsers({ tenantId: activeTenantId }),
      ]);
      setClient(clientRes);
      setUsers(usersRes.users || []);
    } catch (e) {
      setError(e.message || "Failed to load client.");
    } finally {
      setLoading(false);
    }
  }, [activeTenantId, clientId]);

  useEffect(() => { load(); }, [load]);

  function startEdit() {
    setDraft({
      legalName:            client.legalName            || "",
      assignedAccountantUid: client.assignedAccountantUid || "",
      accessLevel:          client.accessLevel          || "senior",
      onboardingStatus:     client.onboardingStatus     || "pending",
      personalProfile:      { ...(client.personalProfile || {}) },
      compliance:           { ...(client.compliance     || {}) },
      taxFinancial:         { ...(client.taxFinancial   || {}) },
      services:             { ...(client.services       || {}) },
    });
    setEditing(true);
    setSaveError("");
  }

  function cancelEdit() {
    setEditing(false);
    setDraft({});
    setSaveError("");
  }

  async function saveEdit() {
    setSaving(true);
    setSaveError("");
    try {
      // Sync legalName from fullLegalName if changed
      const fullName = draft.personalProfile?.fullLegalName || draft.personalProfile?.full_legal_name || "";
      const payload = {
        ...draft,
        legalName: fullName || draft.legalName,
        clientType: "individual",
      };
      const updated = await updateIndividualClient(activeTenantId, clientId, payload);
      setClient(updated);
      setEditing(false);
      setDraft({});
    } catch (e) {
      setSaveError(e.message || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  }

  const userMap = {};
  users.forEach((u) => { userMap[u.uid] = u.displayName || u.email || u.uid; });

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={6} display="flex" justifyContent="center"><LoadingSpinner /></VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error || !client) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={6} textAlign="center">
          <VuiTypography color="error" variant="body2">{error || "Client not found."}</VuiTypography>
          <VuiButton variant="outlined" color="secondary" size="small" onClick={() => history.push("/individual-clients")} sx={{ mt: 2 }}>
            ← Back to Individual Clients
          </VuiButton>
        </VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const pp = client.personalProfile || {};
  const displayName = pp.full_legal_name || client.legalName || "—";
  const preferredName = pp.preferred_name;

  return (
    <DashboardLayout>
      {ConfirmDialog}
      <DashboardNavbar />
      <VuiBox py={3}>

        {/* Back + Header */}
        <VuiBox display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
          <VuiBox>
            <VuiButton
              variant="text" color="secondary" size="small"
              onClick={() => history.push("/individual-clients")}
              sx={{ mb: 1, ml: -1 }}
            >
              <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5 }} /> Individual Clients
            </VuiButton>
            <VuiBox display="flex" alignItems="center" gap={1.5}>
              <VuiBox
                sx={{
                  width: 48, height: 48, borderRadius: "50%",
                  background: "linear-gradient(135deg,#0075ff,#00b3d4)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <PersonIcon sx={{ color: TC.primary, fontSize: 24 }} />
              </VuiBox>
              <VuiBox>
                <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary }}>
                    {displayName}
                  </VuiTypography>
                  <Chip
                    label={(client.status || "active").toUpperCase()}
                    size="small"
                    sx={{
                      height: 20, fontSize: "0.62rem", fontWeight: 700,
                      bgcolor: client.status === "active" ? "rgba(76,175,80,0.15)" : "rgba(158,158,158,0.15)",
                      color: client.status === "active" ? "#4caf50" : "#9e9e9e",
                      border: `1px solid ${client.status === "active" ? "#4caf50" : "#9e9e9e"}`,
                    }}
                  />
                  {client.directorRole && (
                    <Chip
                      label={client.directorRole.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      size="small"
                      sx={{
                        height: 20, fontSize: "0.62rem", fontWeight: 700,
                        bgcolor: "rgba(0,117,255,0.15)", color: TC.linkHover,
                        border: "1px solid rgba(0,117,255,0.4)",
                      }}
                    />
                  )}
                </VuiBox>
                {client.linkedCompanyName && (
                  <VuiBox display="flex" alignItems="center" gap={0.5} mt={0.5}>
                    <VuiTypography variant="caption" sx={{ color: TC.tertiary,  opacity: 0.75 }}>
                      Company:
                    </VuiTypography>
                    <VuiTypography
                      variant="caption"
                      sx={{ color: TC.linkHover, cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => history.push(`/client-profiles/${client.linkedCompanyClientId}`)}
                    >
                      {client.linkedCompanyName}
                    </VuiTypography>
                    {client.directorAppointedOn && (
                      <VuiTypography variant="caption" sx={{ color: TC.tertiary,  opacity: 0.6 }}>
                        · appointed {client.directorAppointedOn}
                      </VuiTypography>
                    )}
                  </VuiBox>
                )}
                {preferredName && (
                  <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                    Known as: {preferredName}
                    {pp.email && ` · ${pp.email}`}
                  </VuiTypography>
                )}
                {!preferredName && pp.email && (
                  <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{pp.email}</VuiTypography>
                )}
              </VuiBox>
            </VuiBox>
          </VuiBox>

          {/* Edit controls */}
          {canManage && (
            <VuiBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
              {!editing ? (
                <VuiButton variant="outlined" color="info" size="small" onClick={startEdit}>
                  <EditIcon sx={{ fontSize: 14, mr: 0.5 }} /> Edit
                </VuiButton>
              ) : (
                <>
                  <VuiButton variant="outlined" color="secondary" size="small" onClick={cancelEdit} disabled={saving}>
                    <CloseIcon sx={{ fontSize: 14, mr: 0.5 }} /> Cancel
                  </VuiButton>
                  <VuiButton variant="contained" color="success" size="small" onClick={saveEdit} disabled={saving}>
                    <SaveIcon sx={{ fontSize: 14, mr: 0.5 }} /> {saving ? "Saving…" : "Save Changes"}
                  </VuiButton>
                </>
              )}
            </VuiBox>
          )}
        </VuiBox>

        {saveError && (
          <VuiBox mb={2} p={1.5} sx={{ background: "rgba(244,67,54,0.12)", border: "1px solid rgba(244,67,54,0.3)", borderRadius: "8px" }}>
            <VuiTypography variant="caption" color="error">{saveError}</VuiTypography>
          </VuiBox>
        )}

        {/* Summary card */}
        <Card sx={{ mb: 3 }}>
          <VuiBox p={2.5}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>Assigned Accountant</VuiTypography>
                {editing ? (
                  <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                    <Select
                      value={draft.assignedAccountantUid || ""}
                      onChange={(e) => setDraft((d) => ({ ...d, assignedAccountantUid: e.target.value }))}
                      displayEmpty
                      sx={SELECT_SX}
                      MenuProps={SELECT_MENU_SX}
                    >
                      <MenuItem value="" sx={{ fontSize: "0.875rem", color: TC.secondary }}>— Unassigned —</MenuItem>
                      {users.map((u) => (
                        <MenuItem key={u.uid} value={u.uid} sx={{ fontSize: "0.875rem" }}>
                          {u.displayName || u.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary }}>
                    {userMap[client.assignedAccountantUid] || "Unassigned"}
                  </VuiTypography>
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>Onboarding</VuiTypography>
                {editing ? (
                  <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                    <Select
                      value={draft.onboardingStatus || "pending"}
                      onChange={(e) => setDraft((d) => ({ ...d, onboardingStatus: e.target.value }))}
                      sx={SELECT_SX}
                      MenuProps={SELECT_MENU_SX}
                    >
                      {ONBOARDING_STATUS.map((o) => (
                        <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Chip
                    label={(client.onboardingStatus || "pending").toUpperCase()}
                    size="small"
                    sx={{
                      height: 20, fontSize: "0.62rem", fontWeight: 700,
                      bgcolor: { pending: "rgba(255,152,0,0.15)", partial: "rgba(33,150,243,0.15)", complete: "rgba(76,175,80,0.15)" }[client.onboardingStatus] || "rgba(158,158,158,0.15)",
                      color:   { pending: "#ff9800", partial: "#2196f3", complete: "#4caf50" }[client.onboardingStatus] || "#9e9e9e",
                      border: "none",
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>AML / KYC</VuiTypography>
                <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary }}>
                  {labelFor(AML_STATUSES, client.compliance?.aml_status || "not_started")}
                </VuiTypography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>Added</VuiTypography>
                <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary }}>
                  {client.createdAt ? new Date(client.createdAt).toLocaleDateString("en-GB") : "—"}
                </VuiTypography>
              </Grid>
            </Grid>
          </VuiBox>
        </Card>

        {/* Tabs */}
        <VuiBox
          sx={{
            borderBottom: "1px solid #e4eef8",
            mb: 3,
          }}
        >
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="inherit"
            sx={{
              "& .MuiTabs-indicator": { backgroundColor: "#0075ff", height: 3, borderRadius: "3px 3px 0 0" },
              "& .MuiTab-root": {
                color: TC.secondary,
                fontSize: "0.78rem",
                fontWeight: 600,
                textTransform: "none",
                minHeight: 44,
                "&.Mui-selected": { color: TC.primary },
              },
              "& .MuiTabScrollButton-root": { color: TC.secondary, opacity: 1 },
            }}
          >
            {TABS.map((t) => <Tab key={t.hash} label={t.label} />)}
          </Tabs>
        </VuiBox>

        {/* Tab content */}
        {tabIndex === 0 && (
          <PersonalTab client={client} editing={editing} draft={draft} setDraft={setDraft} tenantId={activeTenantId} />
        )}
        {tabIndex === 1 && (
          <ComplianceTab client={client} editing={editing} draft={draft} setDraft={setDraft} />
        )}
        {tabIndex === 2 && (
          <TaxTab client={client} editing={editing} draft={draft} setDraft={setDraft} tenantId={activeTenantId} />
        )}

      </VuiBox>
      <Footer />

      {/* Floating add buttons */}
      {canManage && (
        <VuiBox sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 99, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Tooltip title="New Company" placement="left">
            <Fab size="medium" sx={{ bgcolor: "#0075ff", color: "#fff", "&:hover": { bgcolor: "#0060d0" } }}
              onClick={() => history.push("/client-profiles?create=company")}>
              <BusinessIcon />
            </Fab>
          </Tooltip>
          <Tooltip title="New Individual" placement="left">
            <Fab size="medium" sx={{ bgcolor: "#4caf50", color: "#fff", "&:hover": { bgcolor: "#388e3c" } }}
              onClick={() => history.push("/client-profiles?create=individual")}>
              <PersonAddIcon />
            </Fab>
          </Tooltip>
        </VuiBox>
      )}
    </DashboardLayout>
  );
}
